import { type ContactType } from '@/app/models/Contact';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import FeedbackButton from '@/app/components/feedback/FeedbackButton';
import SystemSearch from '@/app/components/inputs/SystemSearch';
import { useSearchHotkeys } from '@/app/hooks/useSearchHotkeys';
import { type AnnouncementRecipientDto } from '@/app/models/AnnouncementRecipientDto';
import { type NotificationDto } from '@/app/models/NotificationDto';
import AnnouncementModal from '@/app/modules/announcements/components/AnnouncementModal';
import { usePendingAnnouncements } from '@/app/modules/announcements/core/_hooks';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { useNotifications } from '@/app/modules/notifications/core/NotificationsContext';
import DailySummaryModal from '@/app/modules/notifications/modal/DailySummaryModal';
import { useTaskNews } from '@/app/modules/task';
import TaskBellIndicator from '@/app/modules/task/components/TaskBellIndicator';
import { useTaskFinishToasts } from '@/app/modules/task/hooks/useTaskFinishToasts';
import SafeImage from '@/app/modules/widgets/SafeImage';
import { listNotification, readNotification } from '@/app/pages/notifications/core/_requests';
import { KTIcon } from '@/_metronic/helpers';
import { ThemeModeSwitcher } from '@/_metronic/partials';
import NotificationsDropdown, {
  type NotificationsTab,
} from '@/_metronic/partials/layout/header-menus/NotificationsDropdown';
import { USER_MENU_BY_USER_TYPE } from '@/_metronic/layout/components/header/core/_consts';

const itemClass = 'ms-1 ms-md-3 d-flex align-items-center';
const btnClass = 'btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px';
const userAvatarClass = 'symbol-35px';
const btnIconClass = 'fs-2';

const FullNavbar = () => {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  // O modal mantém o resumo enquanto some, por isso a visibilidade é um estado à parte.
  const [openSummary, setOpenSummary] = useState<NotificationDto>();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);
  const { currentUser } = useAuth();
  const { hasUnseenNotifications, markNotificationsAsSeen } = useNotifications();
  const queryClient = useQueryClient();
  const { data: pendingAnnouncements = [] } = usePendingAnnouncements();
  const pendingAnnouncementCount = pendingAnnouncements.length;
  // O comunicado aberto pela aba sobrevive ao menu, que se fecha no clique.
  const [openAnnouncement, setOpenAnnouncement] = useState<AnnouncementRecipientDto>();
  const showsBellDot = hasUnseenNotifications || pendingAnnouncementCount > 0;

  // Tarefas em segundo plano moram no mesmo menu das notificações, na aba Tarefas.
  const [notificationsTab, setNotificationsTab] = useState<NotificationsTab>('notifications');
  const taskNews = useTaskNews();
  const { markSeen: markTasksSeen } = taskNews;
  const isTasksTabOpen = isNotificationsOpen && notificationsTab === 'tasks';
  useTaskFinishToasts(taskNews.tasks, isTasksTabOpen);

  // Com a aba aberta, o que termina já está sendo visto.
  useEffect(() => {
    if (isTasksTabOpen) markTasksSeen();
  }, [isTasksTabOpen, markTasksSeen]);

  const toggleNotifications = () => {
    // Abre na aba Tarefas quando há novidade de tarefa; senão, em Notificações, como sempre.
    if (!isNotificationsOpen) setNotificationsTab(taskNews.hasNews ? 'tasks' : 'notifications');
    setIsNotificationsOpen(!isNotificationsOpen);
  };

  // Detect if user is on Mac to show appropriate shortcut hint
  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0);
  }, []);

  useSearchHotkeys(() => setIsSearchOpen(true));

  // Close the user menu when clicking outside its container (trigger + menu).
  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const { data: notificationsData, isPending } = useQuery({
    queryKey: ['notifications', hasUnseenNotifications, isNotificationsOpen],
    queryFn: async () => {
      const response = await listNotification();
      return response.data.content;
    },
    refetchOnWindowFocus: false,
    meta: { suppressGlobalError: true },
  });

  useEffect(() => {
    if (notificationsData) {
      setNotifications(notificationsData);
    }
  }, [notificationsData]);

  const { mutate: sendReadNotificationRequest } = useMutation({
    mutationFn: async (id: string) => {
      const result = await readNotification(id);
      return result;
    },
    onError: (error) => {
      console.error('Erro ao marcar notificação como lida:', error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      });
    },
  });

  const handleNotificationRead = (id: string) => {
    sendReadNotificationRequest(id);
  };

  const handleSummaryOpen = (notification: NotificationDto) => {
    setOpenSummary(notification);
    setIsSummaryOpen(true);
  };

  const UserMenu = USER_MENU_BY_USER_TYPE[currentUser?.type?.value as ContactType];

  return (
    <div className="app-navbar flex-shrink-0 d-flex align-items-center">
      <div className={clsx('app-navbar-item', itemClass)}>
        <FeedbackButton />
      </div>
      <div className={clsx('app-navbar-item', itemClass)}>
        <div
          className="btn btn-custom btn-icon-muted btn-active-light btn-active-color-primary h-35px d-flex align-items-center gap-2"
          style={{ cursor: 'pointer', paddingInline: '10px' }}
          title={`Buscar (${isMac ? '⌘K' : 'Ctrl+K'})`}
          onClick={() => setIsSearchOpen(true)}
        >
          <KTIcon className={btnIconClass} iconName="magnifier" />
          <span
            className="d-none d-md-inline-flex align-items-center gap-1 text-muted fs-8"
            style={{
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: 'var(--bs-gray-200)',
              fontSize: '11px',
              fontWeight: 500,
            }}
          >
            {isMac ? '⌘' : 'Ctrl+'} K
          </span>
        </div>
      </div>
      <div className={clsx('app-navbar-item', itemClass)}>
        <div ref={notificationsRef} className="position-relative">
          <div
            aria-expanded={isNotificationsOpen}
            aria-label="Notificações e tarefas"
            className={clsx('position-relative', btnClass)}
            role="button"
            style={{ cursor: 'pointer' }}
            tabIndex={0}
            onClick={toggleNotifications}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              toggleNotifications();
            }}
          >
            <KTIcon className={btnIconClass} iconName="message-text-2" />
            {showsBellDot ? (
              <span className="bullet bullet-dot bg-danger h-6px w-6px position-absolute translate-middle top-0 start-50 animation-blink" />
            ) : null}
          </div>
          <TaskBellIndicator
            anchorRef={notificationsRef}
            isOpen={isNotificationsOpen}
            running={taskNews.running}
            unseen={taskNews.unseen}
          />
          <NotificationsDropdown
            containerRef={notificationsRef}
            isOpen={isNotificationsOpen}
            loading={isPending}
            notifications={notifications}
            pendingAnnouncements={pendingAnnouncementCount}
            runningTasks={taskNews.running.length}
            tab={notificationsTab}
            unseenTasks={taskNews.unseen}
            onAnnouncementOpen={setOpenAnnouncement}
            onClose={() => {
              setIsNotificationsOpen(false);
              if (hasUnseenNotifications) {
                markNotificationsAsSeen();
              }
            }}
            onNotificationRead={handleNotificationRead}
            onSummaryOpen={handleSummaryOpen}
            onTabChange={setNotificationsTab}
          />
          <DailySummaryModal notification={openSummary} show={isSummaryOpen} onClose={() => setIsSummaryOpen(false)} />
          {openAnnouncement ? (
            <AnnouncementModal
              key={openAnnouncement.id}
              announcement={openAnnouncement}
              onClose={() => setOpenAnnouncement(undefined)}
            />
          ) : null}
        </div>
      </div>
      <div className={clsx('app-navbar-item', itemClass)}>
        <ThemeModeSwitcher
          toggleBtnClass={clsx('btn-icon-muted btn-active-light btn-active-color-primary btn-custom w-35px h-35px')}
          toggleBtnIconClass={btnIconClass}
        />
      </div>
      <div className={clsx('app-navbar-item', itemClass)}>
        <div ref={userMenuRef} className="position-relative">
          <div
            className={clsx('cursor-pointer symbol', userAvatarClass)}
            onClick={() => setIsUserMenuOpen((prev) => !prev)}
          >
            <SafeImage
              alt="Imagem do usuário"
              className="rounded cursor-pointer"
              fallbackName={currentUser?.name}
              height={35}
              src={currentUser?.profile_picture_url}
              width={35}
            />
          </div>
          {UserMenu ? <UserMenu isOpen={isUserMenuOpen} onClose={() => setIsUserMenuOpen(false)} /> : null}
        </div>
      </div>
      <SystemSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </div>
  );
};

export { FullNavbar };
